/**
 * Catalog Error Boundary
 *
 * Handles errors in the catalog page with user-friendly UI and retry options.
 * Captures errors and allows users to recover without full page reload.
 */

'use client';

import React, { useEffect } from 'react';
import Link from 'next/link';
import { HOME_PATH } from '@/lib/config/business';

interface CatalogErrorProps {
  error: Error & { digest?: string };
  reset: () => void;
}

export default function CatalogError({ error, reset }: CatalogErrorProps) {
  useEffect(() => {
    // Log error to console — no external error tracking
    console.error('[Catalog] Error boundary caught:', error);
  }, [error]);

  return (
    <div className="container mx-auto px-4 py-16">
      <div className="max-w-2xl mx-auto text-center">
        {/* Error Icon */}
        <div className="mb-6">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-24 w-24 mx-auto text-red-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>

        {/* Error Message */}
        <h1 className="text-3xl font-bold text-gray-900 mb-4">Something went wrong</h1>
        <p className="text-gray-600 mb-2">An error occurred while loading the product catalog.</p>

        {/* Error Details (dev mode only) */}
        {process.env.NODE_ENV === 'development' && (
          <details className="mt-6 text-left bg-gray-100 rounded-lg p-4 max-w-xl mx-auto">
            <summary className="cursor-pointer font-semibold text-gray-900 mb-2">
              Technical details (developers only)
            </summary>
            <pre className="text-xs text-gray-700 overflow-auto">
              {error.message}
              {error.digest && (
                <div className="mt-2">
                  <strong>Error Digest:</strong> {error.digest}
                </div>
              )}
            </pre>
          </details>
        )}

        {/* Action Buttons */}
        <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
          {/* Retry Button */}
          <button
            onClick={reset}
            className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors duration-150 shadow-md hover:shadow-lg"
          >
            <div className="flex items-center justify-center gap-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              Try again
            </div>
          </button>

          {/* Go Home Button. Главная — каталог (HOME_PATH), а не корень: корень
              лишь редиректит на него. Ошибка могла быть на подстранице каталога,
              и корень каталога — разумное место, откуда начать заново */}
          <Link
            href={HOME_PATH}
            className="px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-900 font-semibold rounded-lg transition-colors duration-150"
          >
            Go to homepage
          </Link>
        </div>

        {/* Help Text */}
        <p className="text-sm text-gray-500 mt-8">
          If the error persists, try{' '}
          <button
            onClick={() => window.location.reload()}
            className="text-blue-600 hover:underline font-medium"
          >
            reloading the page
          </button>
        </p>
      </div>
    </div>
  );
}
